import { useState } from 'react';
import { Gift, Copy, Check, Users, Calendar, Twitter, Linkedin, Mail } from 'lucide-react';
import { useReferral } from '../hooks/useReferral';

export function ReferralDashboard() {
  const { stats, loading, referralLink, copyLink } = useReferral();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const success = await copyLink();
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareText = "I've been using VZMO for personal growth coaching and it's been amazing. Try it free for 37 days with my invite link:";

  const shareOnTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(referralLink || '')}`;
    window.open(url, '_blank', 'width=550,height=420');
  };

  const shareOnLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink || '')}`;
    window.open(url, '_blank', 'width=550,height=420');
  };

  const shareViaEmail = () => {
    const subject = "Try VZMO - Get 37 days free";
    const body = `${shareText}\n\n${referralLink}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center mb-6">
          <Gift className="h-5 w-5 text-gold-500 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Invite Friends</h2>
        </div>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gold-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      <div className="flex items-center mb-6">
        <Gift className="h-5 w-5 text-gold-500 mr-2" />
        <h2 className="text-lg font-semibold text-gray-900">Invite Friends</h2>
      </div>

      <div className="bg-gradient-to-br from-gold-50 to-amber-50 rounded-xl p-6 mb-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 rounded-xl bg-gold-500 flex items-center justify-center flex-shrink-0">
            <Users className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Give 30 days, Get 30 days</h3>
            <p className="text-sm text-gray-600">
              When a friend signs up with your link, you both get an extra month added to your trial.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gold-600">{stats?.total_referrals || 0}</div>
            <div className="text-xs text-gray-500">Total Invites</div>
          </div>
          <div className="bg-white rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-sage-600">{stats?.completed_referrals || 0}</div>
            <div className="text-xs text-gray-500">Signed Up</div>
          </div>
          <div className="bg-white rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-coral-600">{stats?.months_earned || 0}</div>
            <div className="text-xs text-gray-500">Months Earned</div>
          </div>
        </div>

        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">Your invite link</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={referralLink || ''}
              readOnly
              className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none"
            />
            <button
              onClick={handleCopy}
              className="px-4 py-2.5 bg-gold-500 text-ink-950 rounded-lg font-medium text-sm hover:bg-gold-600 transition-colors flex items-center gap-2"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy
                </>
              )}
            </button>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <span className="text-sm text-gray-500">Share via:</span>
            <button
              onClick={shareOnTwitter}
              className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              title="Share on Twitter"
            >
              <Twitter className="h-4 w-4 text-gray-600" />
            </button>
            <button
              onClick={shareOnLinkedIn}
              className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              title="Share on LinkedIn"
            >
              <Linkedin className="h-4 w-4 text-gray-600" />
            </button>
            <button
              onClick={shareViaEmail}
              className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              title="Share via Email"
            >
              <Mail className="h-4 w-4 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      <div className="text-xs text-gray-500 text-center">
        <Calendar className="h-3 w-3 inline mr-1" />
        Referral rewards are automatically applied to your account
      </div>
    </div>
  );
}
